import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faLinkedin,
  faFacebook,
  faInstagram,
  faXTwitter,
  faYoutube,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faBluesky,
} from '@fortawesome/free-brands-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { HorizontalCardCarousel } from './HorizontalCardCarousel';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { HoverCard } from '@/components/ui/HoverCard';
import { RetryImage } from '@/components/ui/RetryImage';
import { PlaceholderImage } from '@/components/ui/PlaceholderImage';
import { DashboardPostPopover } from './DashboardPostPopover';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { useRemixableQueue, RemixableQueueItem } from '@/hooks/useDashboardStats';
import { useTimezone } from '@/contexts/TimezoneContext';
import { formatCompactNumber } from '@/utils/formatNumber';
import { usePostCreatorStore } from '@/stores/postCreatorStore';
import { useDuplicatePost } from '@/hooks/usePosts';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { formatApiError, isGlobalGatingHandledError } from '@/services/api';
import { toast } from '@/utils/toast';

/**
 * Review & Remix Section - Posts ready for remixing
 *
 * Features:
 * - Horizontal carousel with arrow navigation
 * - No period filter (always 2+ weeks old)
 * - Teal theme (primary palette)
 * - FontAwesome platform icons showing where posts were published
 * - Engagement metrics display
 * - HoverCard popover with metrics and remix action
 * - Timezone-aware published dates
 */

// FontAwesome platform icons (same as PublishedSection)
const PLATFORM_FA_ICONS: Record<string, IconDefinition> = {
  LINKEDIN: faLinkedin,
  FACEBOOK: faFacebook,
  INSTAGRAM: faInstagram,
  TWITTER: faXTwitter,
  YOUTUBE: faYoutube,
  TIKTOK: faTiktok,
  PINTEREST: faPinterest,
  THREADS: faThreads,
  GOOGLE_MY_BUSINESS: faGoogle,
  BLUESKY: faBluesky,
};

// Platform colors for icons
const PLATFORM_COLORS: Record<string, string> = {
  LINKEDIN: '#0A66C2',
  FACEBOOK: '#1877F2',
  INSTAGRAM: '#E4405F',
  TWITTER: '#000000',
  YOUTUBE: '#FF0000',
  TIKTOK: '#000000',
  PINTEREST: '#E60023',
  THREADS: '#000000',
  GOOGLE_MY_BUSINESS: '#4285F4',
  BLUESKY: '#0085FF',
};

/**
 * Skeleton Card Component
 */
const SkeletonCard = () => (
  <div className="flex-none w-[160px] aspect-square bg-gray-200 rounded-xl animate-pulse" />
);

/**
 * Post Card Component
 */
interface PostCardProps {
  item: RemixableQueueItem;
  onRemix: (postId: number) => void;
  /** A draft copy of this post is being created */
  isRemixing: boolean;
}

const PostCard = ({ item, onRemix, isRemixing }: PostCardProps) => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { formatRelativeDate } = useTimezone();
  const navigate = useNavigate();

  const handleRemix = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemix(item.postId);
  };

  const handleViewAnalytics = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.channelInstanceId) {
      navigate(`/analytics?post=${item.channelInstanceId}`);
    } else {
      navigate('/analytics');
    }
  };

  // HoverCard popover content - Teal gradient header for remix
  const popoverContent = (
    <DashboardPostPopover
      gradient="from-secondary-500 to-dark-teal"
      headerTone="secondary"
      headerIcon={ICONS.REFRESH}
      headerLabel={t('dashboard:dashboard_remix_ready', { defaultValue: 'Ready to Remix' })}
      headerLines={[
        { icon: ICONS.CALENDAR, text: item.calendarName },
        { icon: ICONS.CLOCK, text: formatRelativeDate(item.publishedAt) },
      ]}
      postMedia={item.postMedia || []}
      caption={item.caption || ''}
      footer={
        <div className="flex gap-2">
          <button
            onClick={handleRemix}
            disabled={isRemixing}
            aria-busy={isRemixing}
            className="flex-1 px-3 py-1.5 bg-secondary-500 text-secondary-contrast text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 hover:bg-secondary-600 disabled:opacity-60 disabled:cursor-wait"
          >
            <Icon name={ICONS.REFRESH} size="sm" className={cn('text-secondary-contrast', isRemixing && 'animate-spin')} ariaHidden />
            {isRemixing
              ? t('dashboard:remix_creating_draft', { defaultValue: 'Creating draft...' })
              : t('dashboard:remix_this_post', { defaultValue: 'Remix This Post' })}
          </button>
          <button
            onClick={handleViewAnalytics}
            className="hidden lg:block px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('dashboard:view_analytics', { defaultValue: 'Analytics' })}
          </button>
        </div>
      }
    >
      {/* Engagement Metrics Section */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <div className="grid grid-cols-2 gap-2 mt-3">
          {/* Always show reach */}
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-500 mb-0.5">Reach</div>
            <div className="text-sm font-bold text-gray-900">
              {item.totalReach > 0 ? formatCompactNumber(item.totalReach) : '—'}
            </div>
          </div>
          {/* Always show engagement */}
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-500 mb-0.5">Engagement</div>
            <div className="text-sm font-bold text-gray-900">
              {item.totalEngagement > 0 ? formatCompactNumber(item.totalEngagement) : '—'}
            </div>
          </div>
          {/* Always show likes */}
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-500 mb-0.5">Likes</div>
            <div className="text-sm font-bold text-gray-900">
              {item.totalLikes > 0 ? formatCompactNumber(item.totalLikes) : '—'}
            </div>
          </div>
          {/* Always show comments */}
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-500 mb-0.5">Comments</div>
            <div className="text-sm font-bold text-gray-900">
              {item.totalComments > 0 ? formatCompactNumber(item.totalComments) : '—'}
            </div>
          </div>
          {/* Show likes if > 0 */}
          {item.totalLikes > 0 && (
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-0.5">Likes</div>
              <div className="text-sm font-bold text-gray-900">{formatCompactNumber(item.totalLikes)}</div>
            </div>
          )}
          {/* Show comments if > 0 */}
          {item.totalComments > 0 && (
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-0.5">Comments</div>
              <div className="text-sm font-bold text-gray-900">{formatCompactNumber(item.totalComments)}</div>
            </div>
          )}
          {/* Show shares if > 0 */}
          {item.totalShares > 0 && (
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-0.5">Shares</div>
              <div className="text-sm font-bold text-gray-900">{formatCompactNumber(item.totalShares)}</div>
            </div>
          )}
          {/* Show saves if > 0 */}
          {item.totalSaves > 0 && (
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-0.5">Saves</div>
              <div className="text-sm font-bold text-gray-900">{formatCompactNumber(item.totalSaves)}</div>
            </div>
          )}
          {/* Show clicks if > 0 */}
          {item.totalClicks > 0 && (
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-0.5">Clicks</div>
              <div className="text-sm font-bold text-gray-900">{formatCompactNumber(item.totalClicks)}</div>
            </div>
          )}
        </div>
        {/* Performance summary */}
        <div className="mt-3 p-2 bg-primary-50 rounded-lg text-center">
          {item.totalReach === 0 && item.totalImpressions === 0 ? (
            <span className="text-xs font-medium text-gray-500">No reach data</span>
          ) : (
            <span className="text-xs font-semibold text-primary-700">{item.engagementRate.toFixed(1)}% ER</span>
          )}
        </div>
      </div>

      {/* Channels section */}
      {item.channels.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            {t('common:published_channels', { defaultValue: 'Published to' })} ({item.channels.length})
          </p>
          <div className="flex flex-wrap gap-2.5">
            {item.channels.map((ch, i) => (
              <Tooltip
                key={i}
                content={
                  <div className="text-xs">
                    <p className="font-medium">{ch.platformUsername || ch.platform}</p>
                    <p className="text-gray-400">{ch.platform}</p>
                    {ch.externalUrl && <p className="text-primary-400 mt-0.5">{t('dashboard:click_to_view', { defaultValue: 'Click to view' })}</p>}
                  </div>
                }
                className="!z-[10001]"
              >
                <div className="relative">
                  <div
                    className={cn(
                      'w-8 h-8 rounded-full overflow-hidden flex items-center justify-center',
                      'ring-2 ring-offset-1 ring-primary-500',
                      ch.externalUrl && 'cursor-pointer hover:opacity-80 transition-opacity'
                    )}
                    onClick={(e) => {
                      if (ch.externalUrl) {
                        e.stopPropagation();
                        window.open(ch.externalUrl, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    {ch.profilePhotoUrl ? (
                      <img src={ch.profilePhotoUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <FontAwesomeIcon
                          icon={PLATFORM_FA_ICONS[ch.platform] || faGlobe}
                          className="text-gray-500 w-4 h-4"
                          aria-hidden="true"
                        />
                      </div>
                    )}
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-white shadow-sm flex items-center justify-center">
                    <FontAwesomeIcon
                      icon={PLATFORM_FA_ICONS[ch.platform] || faGlobe}
                      className="text-gray-600 text-[10px]"
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </Tooltip>
            ))}
          </div>
        </div>
      )}
    </DashboardPostPopover>
  );

  // Card trigger - Square layout with full-bleed thumbnail
  const cardTrigger = (
    <div className="flex-none w-[160px] aspect-square rounded-xl overflow-hidden relative bg-gray-100 cursor-pointer hover:shadow-lg hover:scale-[1.03] transition-all duration-200">
      {/* Full-bleed thumbnail */}
      {item.thumbnailUrl ? (
        <RetryImage
          src={item.thumbnailUrl}
          alt=""
          className="w-full h-full object-cover"
          fallback={<PlaceholderImage />}
        />
      ) : (
        <PlaceholderImage />
      )}

      {/* Top overlay: Channel avatars */}
      <div className="absolute top-0 inset-x-0 p-2 flex items-center gap-1">
        {item.channels.slice(0, 3).map((ch, index) => (
          <Tooltip key={`${ch.platform}-${index}`} content={ch.platformUsername || ch.platform}>
          <div
            className={cn(
              "w-6 h-6 rounded-full overflow-hidden bg-white/90 backdrop-blur-sm flex items-center justify-center flex-shrink-0 ring-1 ring-white/50",
              ch.externalUrl && "cursor-pointer hover:ring-primary-300"
            )}
            onClick={(e) => {
              if (ch.externalUrl) {
                e.stopPropagation();
                window.open(ch.externalUrl, '_blank', 'noopener,noreferrer');
              }
            }}
          >
            {ch.profilePhotoUrl ? (
              <img src={ch.profilePhotoUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <FontAwesomeIcon
                icon={PLATFORM_FA_ICONS[ch.platform] || faGlobe}
                className="w-3 h-3"
                style={{ color: PLATFORM_COLORS[ch.platform] || '#6B7280' }}
                aria-hidden="true"
              />
            )}
          </div>
          </Tooltip>
        ))}
        {item.channels.length > 3 && (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/90 backdrop-blur-sm text-[10px] font-semibold text-gray-600 ring-1 ring-white/50">
            +{item.channels.length - 3}
          </span>
        )}
      </div>

      {/* Bottom gradient overlay */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-2 pt-8">
        {/* Reach / Impressions badge */}
        <div className="flex items-center gap-1 mb-0.5">
          <Icon name={ICONS.CHART} size="xs" className="text-white" ariaHidden />
          <span className="text-[11px] text-white font-medium drop-shadow-sm">
            {item.totalReach === 0 && item.totalImpressions === 0
              ? `${formatCompactNumber(item.totalEngagement)} engmt`
              : `${formatCompactNumber(item.totalReach > 0 ? item.totalReach : item.totalImpressions)} ${item.totalReach > 0 ? 'reach' : 'impr.'}`
            }
          </span>
        </div>
        {/* Published date */}
        <div className="flex items-center gap-1 mb-0.5">
          <Icon name={ICONS.CLOCK} size="xs" className="text-primary-300" ariaHidden />
          <span className="text-[10px] text-white/80 drop-shadow-sm">
            {formatRelativeDate(item.publishedAt)}
          </span>
        </div>
        {/* Caption */}
        <p className={cn(
          'text-[11px] text-white/90 line-clamp-1 drop-shadow-sm',
          !item.caption && 'italic'
        )}>
          {item.caption ? (item.caption.length > 40 ? `${item.caption.slice(0, 40)}...` : item.caption) : t('common:published_no_caption', { defaultValue: 'No caption' })}
        </p>
      </div>
    </div>
  );

  return (
    <HoverCard
      trigger={cardTrigger}
      side="right"
      align="start"
    >
      {popoverContent}
    </HoverCard>
  );
};

/**
 * Empty State
 */
const EmptyState = () => {
  const { t } = useTranslation('dashboard');

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full grid place-items-center mb-4">
        <Icon name={ICONS.REFRESH} size="xl" color="text-gray-400" ariaHidden />
      </div>
      <p className="text-gray-900 font-medium mb-1">
        {t('dashboard_remix_empty_title', { defaultValue: 'No posts ready to remix yet' })}
      </p>
      <p className="text-gray-500 text-sm">
        {t('dashboard_remix_empty_subtitle', { defaultValue: 'Posts published 2+ weeks ago will appear here' })}
      </p>
    </div>
  );
};

/**
 * Main Review & Remix Section Component
 */
export const ReviewRemixSection = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const navigate = useNavigate();
  const { data, isLoading } = useRemixableQueue();

  // PostCreator store
  const openPostCreator = usePostCreatorStore((s) => s.open);

  const duplicatePost = useDuplicatePost();
  const { teamId: effectiveTeamId } = useEffectiveTeamId();
  // Latest brand, read after the request returns (the user may switch brand while the copy is created).
  const effectiveTeamIdRef = useRef(effectiveTeamId);
  effectiveTeamIdRef.current = effectiveTeamId;
  const [remixingPostId, setRemixingPostId] = useState<number | null>(null);

  // A published original is read-only, so Remix edits a DRAFT COPY of it (the original stays untouched).
  const handleRemix = async (postId: number): Promise<void> => {
    if (remixingPostId !== null) return;
    setRemixingPostId(postId);
    const teamAtClick = effectiveTeamIdRef.current;
    try {
      const copy = await duplicatePost.mutateAsync(postId);
      // Open the copy only in the brand it was made for: never brand A's draft while brand B is selected.
      if (effectiveTeamIdRef.current !== teamAtClick || (copy.teamId !== undefined && copy.teamId !== teamAtClick)) {
        toast.info(t('common:composer_closed_brand_changed'));
        return;
      }
      openPostCreator({ editingPostId: copy.id });
    } catch (error) {
      if (isGlobalGatingHandledError(error)) return;
      toast.error(formatApiError(error).message || t('dashboard:remix_failed', { defaultValue: 'Could not create a draft copy. Please try again.' }));
    } finally {
      setRemixingPostId(null);
    }
  };

  const handleViewAll = () => {
    navigate('/creator');
  };

  return (
    <div>
      {isLoading && (
        <div className="flex gap-3 overflow-x-hidden">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {!isLoading && data?.items.length === 0 && <EmptyState />}

      {!isLoading && data && data.items.length > 0 && (
        <HorizontalCardCarousel>
          {data.items.map((item) => (
            <PostCard
              key={item.id}
              item={item}
              onRemix={(id) => void handleRemix(id)}
              isRemixing={remixingPostId === item.postId}
            />
          ))}
        </HorizontalCardCarousel>
      )}

      <div className="mt-3 text-center">
        <button
          onClick={handleViewAll}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
        >
          {t('dashboard_remix_view_all', { defaultValue: 'View all in Creator →' })}
        </button>
      </div>
    </div>
  );
};
